import React, {useMemo, useState} from 'react';
import styled from 'styled-components';
import {uniqueId} from 'lodash';
import {useIntl} from 'react-intl';
import {Button, EMPHASIS, Tooltip} from '@forecast-it/design-system';
import {ActiveMark, useActiveTip, VisuallyHidden} from './ActiveMark';
import {VersionName} from './VersionName';
import {C, F} from './versionsStyles';
import {MAX_VERSIONS} from './baselineVersionsMath';

export interface TabVersion {
	readonly id: string;
	readonly name: string | null;
	readonly isActive: boolean | null;
}

export interface BaselineVersionTabsProps {
	versions: ReadonlyArray<TabVersion>;
	selectedId: string | null;
	locked: boolean;
	quotesEnabled: boolean;
	panelOpen: boolean;
	compareOpen: boolean;
	onSelect: (id: string) => void;
	onNewVersion: () => void;
	onToggleCompare: () => void;
}

const Bar = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: stretch;
	gap: 8px;
	border-bottom: 1px solid ${C.borderDisabled};
`;

const TabList = styled.div`
	display: flex;
	align-items: stretch;
	overflow-x: auto;
	scrollbar-width: none;
`;

const Tab = styled.div<{$selected: boolean}>`
	display: flex;
	align-items: center;
	gap: 4px;
	height: 48px;
	padding: 8px 12px;
	box-sizing: border-box;
	cursor: pointer;
	white-space: nowrap;
	background: ${C.surface};
	color: ${C.text};
	border-bottom: 4px solid ${p => (p.$selected ? C.brand : 'transparent')};
	${p => (p.$selected ? F.sub1 : F.body1)}
	&:focus-visible {
		outline: 2px solid ${C.brand};
		outline-offset: -2px;
	}
`;

const Focusable = styled.span`
	display: flex;
	align-items: center;
`;

/** Long names are cut off here, with the full name in the tab's tooltip. */
const NAME_MAX_WIDTH = '200px';

const CompareSlot = styled.span`
	display: flex;
	align-items: center;
	margin-left: auto;
`;

/**
 * A disabled control's reason, readable on hover and on keyboard focus. The reason describes the wrapper rather than
 * naming it, so a screen reader still announces the control's own name.
 */
const WithReason = ({reason, children}: {reason: string | null; children: React.ReactElement}) => {
	const reasonId = useMemo(() => uniqueId('baseline-version-reason-'), []);
	return reason ? (
		<Tooltip content={reason} width="260px">
			<Focusable tabIndex={0} aria-describedby={reasonId}>
				{children}
				<VisuallyHidden id={reasonId}>{reason}</VisuallyHidden>
			</Focusable>
		</Tooltip>
	) : (
		children
	);
};

interface VersionTabProps {
	version: TabVersion;
	selected: boolean;
	locked: boolean;
	activeTip: string;
	activeTipId: string;
	onSelect: (id: string) => void;
}

/**
 * One version tab. The tab is the only focus stop: while the name is cut off, the tab itself carries a tooltip with the
 * full name, so it shows on keyboard focus as well as on hover. The active tab always carries the active tip, joined
 * with the full name in one tooltip while the name is cut off.
 */
const VersionTab = ({version, selected, locked, activeTip, activeTipId, onSelect}: VersionTabProps) => {
	const [cut, setCut] = useState(false);
	const name = version.name ?? '';
	const tab = (
		<Tab
			role="tab"
			tabIndex={0}
			aria-selected={selected}
			aria-describedby={version.isActive ? activeTipId : undefined}
			$selected={selected}
			data-cy={`baseline-version-tab-${version.name}`}
			onClick={() => onSelect(version.id)}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onSelect(version.id);
				}
			}}
		>
			<VersionName name={name} maxWidth={NAME_MAX_WIDTH} onCutChange={setCut} />
			{version.isActive ? <ActiveMark locked={locked} /> : null}
		</Tab>
	);
	const tip = version.isActive ? cut ? <Tooltip.Item title={name} details={activeTip} /> : activeTip : cut ? name : null;
	return tip ? (
		<Tooltip content={tip} width="260px">
			{tab}
		</Tooltip>
	) : (
		tab
	);
};

export const BaselineVersionTabs = ({
	versions,
	selectedId,
	locked,
	quotesEnabled,
	panelOpen,
	compareOpen,
	onSelect,
	onNewVersion,
	onToggleCompare,
}: BaselineVersionTabsProps) => {
	const {formatMessage} = useIntl();
	const full = versions.length >= MAX_VERSIONS;
	const lockedTip = formatMessage({id: 'baseline.versions.locked_tooltip'});
	const newVersionReason = locked
		? lockedTip
		: full
		? formatMessage({id: 'baseline.versions.limit_reached'}, {max: MAX_VERSIONS})
		: null;
	const activeTip = useActiveTip(quotesEnabled);
	const activeTipId = useMemo(() => uniqueId('baseline-version-active-tip-'), []);

	return (
		<Bar data-cy="baseline-version-tabs">
			<TabList role="tablist" aria-label={formatMessage({id: 'baseline.versions.tabs_label'})}>
				{versions.map(v => (
					<VersionTab
						key={v.id}
						version={v}
						selected={v.id === selectedId}
						locked={locked}
						activeTip={activeTip}
						activeTipId={activeTipId}
						onSelect={onSelect}
					/>
				))}
			</TabList>
			<VisuallyHidden id={activeTipId}>{activeTip}</VisuallyHidden>
			<WithReason reason={newVersionReason}>
				<Button
					emphasis={EMPHASIS.LOW}
					icon={locked ? 'lockClosed' : 'plus'}
					disabled={locked || full || panelOpen}
					onPress={onNewVersion}
					data-cy="baseline-new-version"
				>
					{formatMessage({id: 'baseline.versions.new_version'})}
				</Button>
			</WithReason>
			<CompareSlot>
				<Button
					emphasis={EMPHASIS.LOW}
					icon="grid"
					aria-pressed={compareOpen}
					disabled={versions.length < 2}
					onPress={onToggleCompare}
					data-cy="baseline-compare-toggle"
				>
					{formatMessage({id: 'baseline.versions.compare'})}
				</Button>
			</CompareSlot>
		</Bar>
	);
};
